import {
  assertDesignMutationExecutable,
  computeDesignContentHash,
  type DesignMutation,
  type DesignMutationActor,
  type DesignMutationGeometry,
} from "./design-mutation";

const SHA256_DIGEST_PATTERN = /^[a-f0-9]{64}$/;
const CONTENT_HASH_PATTERN = /^sha256:[a-f0-9]{64}$/;
const MAX_PATCH_OPERATIONS = 50;
const MAX_PATCH_BYTES = 80_000;
const MAX_ID_LENGTH = 500;
const MAX_REASON_LENGTH = 2_000;
const MAX_TEXT_LENGTH = 20_000;
const MAX_COORDINATE = 1_000_000;
const MAX_DIMENSION = 1_000_000;
const MAX_ROTATION = 360;

export type FigmaCompanionOperatorScope =
  | "apply"
  | "structural"
  | "destructive";

export type FigmaCompanionOperationKind =
  | "rename-node"
  | "set-text"
  | "set-visible"
  | "set-opacity"
  | "set-geometry"
  | "delete-node"
  | "reparent-node"
  | "reorder-node";

interface FigmaCompanionOperationBase {
  operationId: string;
  kind: FigmaCompanionOperationKind;
  nodeId: string;
  expectedNodeType: string;
  expectedName?: string;
  /** SHA-256 of the exact checkpoint snapshot for this node. */
  expectedNodeHash: string;
}

export interface FigmaCompanionRenameNodeOperation extends FigmaCompanionOperationBase {
  kind: "rename-node";
  nextValue: string;
}

export interface FigmaCompanionSetTextOperation extends FigmaCompanionOperationBase {
  kind: "set-text";
  nextValue: string;
}

export interface FigmaCompanionSetVisibleOperation extends FigmaCompanionOperationBase {
  kind: "set-visible";
  nextValue: boolean;
}

export interface FigmaCompanionSetOpacityOperation extends FigmaCompanionOperationBase {
  kind: "set-opacity";
  nextValue: number;
}

export interface FigmaCompanionSetGeometryOperation extends FigmaCompanionOperationBase {
  kind: "set-geometry";
  nextValue: DesignMutationGeometry;
}

export interface FigmaCompanionDeleteNodeOperation extends FigmaCompanionOperationBase {
  kind: "delete-node";
  expectedParentNodeId: string;
  expectedIndex: number;
  expectedParentChildrenHash: string;
}

export interface FigmaCompanionReparentNodeOperation extends FigmaCompanionOperationBase {
  kind: "reparent-node";
  expectedParentNodeId: string;
  expectedIndex: number;
  expectedParentChildrenHash: string;
  nextParentNodeId: string;
  expectedNextParentChildrenHash: string;
  nextIndex: number;
}

export interface FigmaCompanionReorderNodeOperation extends FigmaCompanionOperationBase {
  kind: "reorder-node";
  parentNodeId: string;
  expectedIndex: number;
  expectedParentChildrenHash: string;
  nextIndex: number;
}

export type FigmaCompanionOperation =
  | FigmaCompanionRenameNodeOperation
  | FigmaCompanionSetTextOperation
  | FigmaCompanionSetVisibleOperation
  | FigmaCompanionSetOpacityOperation
  | FigmaCompanionSetGeometryOperation
  | FigmaCompanionDeleteNodeOperation
  | FigmaCompanionReparentNodeOperation
  | FigmaCompanionReorderNodeOperation;

export interface FigmaCompanionSourceAuthority {
  fileKey: string;
  expectedVersion: string;
  /** SHA-256 of the exact selected-node checkpoint, without a prefix. */
  expectedHash: string;
  openPencilSourceId: string;
  openPencilSourceRevision: number;
  openPencilSourceSha256: string;
}

export interface FigmaCompanionSelectionAuthority {
  nodeIds: string[];
  hash: string;
  tombstoneNodeIds?: string[];
}

export interface FigmaCompanionSemanticAuthority {
  kind: "cogo-design-mutation" | "cogo-design-patch";
  ref: string;
  contentHash: string;
}

/**
 * A Studio-produced, content-addressed proposal. It is not an authenticated
 * Figma write authority. The Companion creates a separate live operator
 * confirmation from figma.currentUser immediately before execution.
 */
export interface FigmaCompanionPatch {
  schemaVersion: "cogo.figma-companion-patch.v1";
  patchId: string;
  idempotencyKey: string;
  source: FigmaCompanionSourceAuthority;
  selection: FigmaCompanionSelectionAuthority;
  semanticAuthority: FigmaCompanionSemanticAuthority;
  actor: DesignMutationActor;
  reason: string;
  operations: FigmaCompanionOperation[];
  requestedAt: string;
  requiredOperatorScopes: FigmaCompanionOperatorScope[];
  contentHash: string;
}

export interface CreateFigmaCompanionPatchInput {
  patchId: string;
  idempotencyKey: string;
  source: FigmaCompanionSourceAuthority;
  selection: FigmaCompanionSelectionAuthority;
  semanticAuthority: FigmaCompanionSemanticAuthority;
  actor: DesignMutationActor;
  reason: string;
  operations: FigmaCompanionOperation[];
  requestedAt: string;
}

export interface CreateFigmaCompanionPatchFromDesignMutationInput {
  mutation: DesignMutation;
  localRevision: {
    sourceId: string;
    sourceRevision: number;
    sha256: string;
  };
  checkpoint: {
    fileKey: string;
    version: string;
    hash: string;
    nodeIds: string[];
    nodeAuthorities: Record<string, FigmaCompanionNodeAuthority>;
  };
  patchId: string;
  idempotencyKey: string;
}

export interface FigmaCompanionNodeAuthority {
  nodeHash: string;
  parentNodeId: string | null;
  index: number | null;
  parentChildrenHash: string | null;
}

export interface FigmaCompanionOperatorIdentity {
  source: "figma-current-user";
  userId: string;
  userName: string;
}

export interface FigmaCompanionOperatorConfirmation {
  schemaVersion: "cogo.figma-companion-operator-confirmation.v1";
  patchId: string;
  patchHash: string;
  confirmedBy: FigmaCompanionOperatorIdentity;
  confirmedAt: string;
  scopes: FigmaCompanionOperatorScope[];
  contentHash: string;
}

export interface CreateFigmaCompanionOperatorConfirmationInput {
  confirmedBy: FigmaCompanionOperatorIdentity;
  confirmedAt: string;
  scopes: FigmaCompanionOperatorScope[];
}

export interface FigmaCompanionObservedSourceAuthority {
  fileKey: string;
  version: string;
  hash: string;
}

export interface FigmaCompanionObservedSelectionAuthority {
  nodeIds: string[];
  hash: string;
  tombstoneNodeIds: string[];
}

export interface FigmaCompanionOperationReadback {
  operationId: string;
  kind: FigmaCompanionOperationKind;
  nodeId: string;
  observedValue: unknown;
}

export interface FigmaCompanionReceipt {
  schemaVersion: "cogo.figma-companion-receipt.v1";
  receiptId: string;
  patchId: string;
  patchHash: string;
  idempotencyKey: string;
  operatorConfirmationHash: string;
  baseSource: FigmaCompanionObservedSourceAuthority;
  resultSource: FigmaCompanionObservedSourceAuthority & {
    versionHistoryId: string;
  };
  resultSelection: FigmaCompanionObservedSelectionAuthority;
  operationReadbacks: FigmaCompanionOperationReadback[];
  appliedBy: FigmaCompanionOperatorIdentity;
  appliedAt: string;
  status: "applied";
  contentHash: string;
}

export interface CreateFigmaCompanionReceiptInput {
  receiptId: string;
  resultSource: FigmaCompanionReceipt["resultSource"];
  resultSelection: FigmaCompanionObservedSelectionAuthority;
  operationReadbacks: FigmaCompanionOperationReadback[];
  appliedAt: string;
}

export type FigmaCompanionExecutionPlan = {
  status: "ready";
  patch: FigmaCompanionPatch;
  operatorConfirmation: FigmaCompanionOperatorConfirmation;
};

export interface PrepareFigmaCompanionExecutionInput {
  patch: FigmaCompanionPatch;
  operatorConfirmation: FigmaCompanionOperatorConfirmation;
  observedSource: FigmaCompanionObservedSourceAuthority;
  observedSelection: FigmaCompanionObservedSelectionAuthority;
}

export function createFigmaCompanionPatch(
  input: CreateFigmaCompanionPatchInput,
): FigmaCompanionPatch {
  assertCreatePatchInput(input);
  const operations = input.operations.map(normalizeOperation);
  const base = {
    schemaVersion: "cogo.figma-companion-patch.v1" as const,
    patchId: input.patchId,
    idempotencyKey: input.idempotencyKey,
    source: { ...input.source },
    selection: {
      nodeIds: uniqueSorted(input.selection.nodeIds),
      hash: input.selection.hash,
    },
    semanticAuthority: { ...input.semanticAuthority },
    actor: { ...input.actor },
    reason: input.reason,
    operations,
    requestedAt: input.requestedAt,
    requiredOperatorScopes: requiredOperatorScopes(operations),
  };
  const patch = { ...base, contentHash: computeDesignContentHash(base) };
  assertPayloadSize(patch);
  return patch;
}

export function createFigmaCompanionPatchFromDesignMutation(
  input: CreateFigmaCompanionPatchFromDesignMutationInput,
): FigmaCompanionPatch {
  assertDesignMutationExecutable(input.mutation);
  const source = input.mutation.source;
  if (source.kind !== "openpencil-revision") {
    throw new Error("FIGMA_COMPANION_OPENPENCIL_REVISION_REQUIRED");
  }
  if (!source.upstreamFigma) {
    throw new Error("FIGMA_COMPANION_UPSTREAM_FIGMA_REQUIRED");
  }
  assertBoundedString(
    input.localRevision.sourceId,
    "FIGMA_COMPANION_OPENPENCIL_SOURCE_ID",
  );
  assertNonNegativeInteger(
    input.localRevision.sourceRevision,
    "FIGMA_COMPANION_OPENPENCIL_SOURCE_REVISION",
  );
  assertSha256(input.localRevision.sha256, "FIGMA_COMPANION_OPENPENCIL_SOURCE");
  if (
    input.localRevision.sourceId === source.sourceId ||
    input.localRevision.sourceRevision <= source.sourceRevision
  ) {
    throw new Error("FIGMA_COMPANION_FINALIZED_LOCAL_REVISION_REQUIRED");
  }
  if (source.upstreamFigma.fileKey !== input.checkpoint.fileKey) {
    throw new Error("FIGMA_COMPANION_FILE_KEY_CONFLICT");
  }

  assertCheckpointNodeAuthorities(
    input.checkpoint.nodeIds,
    input.checkpoint.nodeAuthorities,
  );
  const mutationNodeIds = uniqueSorted(
    input.mutation.operations.map((operation) => operation.sourceTarget.nodeId),
  );
  if (!sameStrings(mutationNodeIds, uniqueSorted(input.checkpoint.nodeIds))) {
    throw new Error("FIGMA_COMPANION_SELECTED_NODE_SET_MISMATCH");
  }
  const operations = input.mutation.operations.map((operation) => {
    const authority =
      input.checkpoint.nodeAuthorities[operation.sourceTarget.nodeId];
    if (!authority) {
      throw new Error("FIGMA_COMPANION_CHECKPOINT_NODE_AUTHORITY_REQUIRED");
    }
    return {
      operationId: operation.operationId,
      kind: operation.kind,
      nodeId: operation.sourceTarget.nodeId,
      expectedNodeType: operation.sourceTarget.expectedNodeType,
      ...(operation.sourceTarget.expectedName !== undefined
        ? { expectedName: operation.sourceTarget.expectedName }
        : {}),
      expectedNodeHash: authority.nodeHash,
      nextValue:
        operation.kind === "set-geometry"
          ? { ...operation.nextValue }
          : operation.nextValue,
    };
  }) as FigmaCompanionOperation[];
  return createFigmaCompanionPatch({
    patchId: input.patchId,
    idempotencyKey: input.idempotencyKey,
    source: {
      fileKey: input.checkpoint.fileKey,
      expectedVersion: input.checkpoint.version,
      expectedHash: input.checkpoint.hash,
      openPencilSourceId: input.localRevision.sourceId,
      openPencilSourceRevision: input.localRevision.sourceRevision,
      openPencilSourceSha256: input.localRevision.sha256,
    },
    selection: {
      nodeIds: input.checkpoint.nodeIds,
      hash: input.checkpoint.hash,
    },
    semanticAuthority: {
      kind: "cogo-design-mutation",
      ref: input.mutation.mutationId,
      contentHash: input.mutation.contentHash,
    },
    actor: input.mutation.actor,
    reason: input.mutation.reason,
    operations,
    requestedAt: input.mutation.requestedAt,
  });
}

export function createFigmaCompanionOperatorConfirmation(
  patch: FigmaCompanionPatch,
  input: CreateFigmaCompanionOperatorConfirmationInput,
): FigmaCompanionOperatorConfirmation {
  assertFigmaCompanionPatch(patch);
  assertOperatorIdentity(input.confirmedBy);
  assertIsoTimestamp(input.confirmedAt, "FIGMA_COMPANION_CONFIRMED_AT");
  assertOperatorScopes(input.scopes);
  const scopes = uniqueSorted(input.scopes) as FigmaCompanionOperatorScope[];
  assertRequiredOperatorScopes(patch, scopes);
  const base = {
    schemaVersion: "cogo.figma-companion-operator-confirmation.v1" as const,
    patchId: patch.patchId,
    patchHash: patch.contentHash,
    confirmedBy: { ...input.confirmedBy },
    confirmedAt: input.confirmedAt,
    scopes,
  };
  return { ...base, contentHash: computeDesignContentHash(base) };
}

export function prepareFigmaCompanionExecution(
  input: PrepareFigmaCompanionExecutionInput,
): FigmaCompanionExecutionPlan {
  assertFigmaCompanionPatch(input.patch);
  verifyFigmaCompanionOperatorConfirmation(
    input.patch,
    input.operatorConfirmation,
  );
  assertObservedSource(input.observedSource, "FIGMA_COMPANION_OBSERVED_SOURCE");
  assertObservedSelection(input.observedSelection);
  if (input.observedSource.fileKey !== input.patch.source.fileKey) {
    throw new Error("FIGMA_COMPANION_FILE_KEY_CONFLICT");
  }
  if (input.observedSource.version !== input.patch.source.expectedVersion) {
    throw new Error("FIGMA_COMPANION_SOURCE_VERSION_CONFLICT");
  }
  if (input.observedSource.hash !== input.patch.source.expectedHash) {
    throw new Error("FIGMA_COMPANION_SOURCE_HASH_CONFLICT");
  }
  if (input.observedSelection.hash !== input.patch.selection.hash) {
    throw new Error("FIGMA_COMPANION_SELECTION_HASH_CONFLICT");
  }
  if (input.observedSelection.tombstoneNodeIds.length > 0) {
    throw new Error("FIGMA_COMPANION_BASE_SELECTION_TOMBSTONE_CONFLICT");
  }
  if (
    !sameStrings(
      uniqueSorted(input.observedSelection.nodeIds),
      input.patch.selection.nodeIds,
    )
  ) {
    throw new Error("FIGMA_COMPANION_SELECTED_NODE_SET_MISMATCH");
  }
  return {
    status: "ready",
    patch: input.patch,
    operatorConfirmation: input.operatorConfirmation,
  };
}

export function verifyFigmaCompanionOperatorConfirmation(
  patch: FigmaCompanionPatch,
  confirmation: FigmaCompanionOperatorConfirmation,
): void {
  assertFigmaCompanionPatch(patch);
  assertFigmaCompanionOperatorConfirmation(confirmation);
  assertConfirmationForPatch(patch, confirmation);
}

export function createFigmaCompanionReceipt(
  patch: FigmaCompanionPatch,
  operatorConfirmation: FigmaCompanionOperatorConfirmation,
  input: CreateFigmaCompanionReceiptInput,
): FigmaCompanionReceipt {
  assertFigmaCompanionPatch(patch);
  assertFigmaCompanionOperatorConfirmation(operatorConfirmation);
  assertConfirmationForPatch(patch, operatorConfirmation);
  assertBoundedString(input.receiptId, "FIGMA_COMPANION_RECEIPT_ID");
  assertIsoTimestamp(input.appliedAt, "FIGMA_COMPANION_APPLIED_AT");
  assertObservedSource(input.resultSource, "FIGMA_COMPANION_RESULT_SOURCE", [
    "versionHistoryId",
  ]);
  assertBoundedString(
    input.resultSource.versionHistoryId,
    "FIGMA_COMPANION_VERSION_HISTORY_ID",
  );
  assertObservedSelection(input.resultSelection);
  if (input.resultSource.fileKey !== patch.source.fileKey) {
    throw new Error("FIGMA_COMPANION_RESULT_FILE_KEY_MISMATCH");
  }
  if (input.resultSource.version === patch.source.expectedVersion) {
    throw new Error("FIGMA_COMPANION_NEW_SOURCE_VERSION_REQUIRED");
  }
  if (input.resultSource.hash !== input.resultSelection.hash) {
    throw new Error("FIGMA_COMPANION_RESULT_SELECTION_HASH_MISMATCH");
  }
  assertResultSelection(patch, input.resultSelection);
  assertOperationReadbacks(patch.operations, input.operationReadbacks);

  const base = {
    schemaVersion: "cogo.figma-companion-receipt.v1" as const,
    receiptId: input.receiptId,
    patchId: patch.patchId,
    patchHash: patch.contentHash,
    idempotencyKey: patch.idempotencyKey,
    operatorConfirmationHash: operatorConfirmation.contentHash,
    baseSource: {
      fileKey: patch.source.fileKey,
      version: patch.source.expectedVersion,
      hash: patch.source.expectedHash,
    },
    resultSource: { ...input.resultSource },
    resultSelection: cloneJsonValue(input.resultSelection),
    operationReadbacks: input.operationReadbacks.map((readback) =>
      cloneJsonValue(readback),
    ),
    appliedBy: { ...operatorConfirmation.confirmedBy },
    appliedAt: input.appliedAt,
    status: "applied" as const,
  };
  return { ...base, contentHash: computeDesignContentHash(base) };
}

export function assertFigmaCompanionPatch(
  value: unknown,
): asserts value is FigmaCompanionPatch {
  assertRecord(value, "FIGMA_COMPANION_PATCH");
  assertExactKeys(
    value,
    [
      "schemaVersion",
      "patchId",
      "idempotencyKey",
      "source",
      "selection",
      "semanticAuthority",
      "actor",
      "reason",
      "operations",
      "requestedAt",
      "requiredOperatorScopes",
      "contentHash",
    ],
    "FIGMA_COMPANION_PATCH",
  );
  if (value.schemaVersion !== "cogo.figma-companion-patch.v1") {
    throw new Error("INVALID_FIGMA_COMPANION_PATCH_SCHEMA_VERSION");
  }
  const { contentHash, ...base } = value;
  assertContentHash(contentHash, "FIGMA_COMPANION_PATCH");
  assertCreatePatchInput(base as unknown as CreateFigmaCompanionPatchInput);
  const required = requiredOperatorScopes(
    value.operations as FigmaCompanionOperation[],
  );
  assertOperatorScopes(value.requiredOperatorScopes);
  if (!sameStrings(value.requiredOperatorScopes as string[], required)) {
    throw new Error("FIGMA_COMPANION_REQUIRED_OPERATOR_SCOPES_MISMATCH");
  }
  if (computeDesignContentHash(base) !== contentHash) {
    throw new Error("INVALID_FIGMA_COMPANION_PATCH_CONTENT_HASH");
  }
  assertPayloadSize(value);
}

export function assertFigmaCompanionOperatorConfirmation(
  value: unknown,
): asserts value is FigmaCompanionOperatorConfirmation {
  assertRecord(value, "FIGMA_COMPANION_OPERATOR_CONFIRMATION");
  assertExactKeys(
    value,
    [
      "schemaVersion",
      "patchId",
      "patchHash",
      "confirmedBy",
      "confirmedAt",
      "scopes",
      "contentHash",
    ],
    "FIGMA_COMPANION_OPERATOR_CONFIRMATION",
  );
  if (value.schemaVersion !== "cogo.figma-companion-operator-confirmation.v1") {
    throw new Error(
      "INVALID_FIGMA_COMPANION_OPERATOR_CONFIRMATION_SCHEMA_VERSION",
    );
  }
  assertBoundedString(value.patchId, "FIGMA_COMPANION_PATCH_ID");
  assertContentHash(value.patchHash, "FIGMA_COMPANION_PATCH");
  assertOperatorIdentity(value.confirmedBy);
  assertIsoTimestamp(value.confirmedAt, "FIGMA_COMPANION_CONFIRMED_AT");
  assertOperatorScopes(value.scopes);
  assertContentHash(value.contentHash, "FIGMA_COMPANION_OPERATOR_CONFIRMATION");
  const { contentHash, ...base } = value;
  if (computeDesignContentHash(base) !== contentHash) {
    throw new Error(
      "INVALID_FIGMA_COMPANION_OPERATOR_CONFIRMATION_CONTENT_HASH",
    );
  }
}

export function assertFigmaCompanionReceipt(
  value: unknown,
): asserts value is FigmaCompanionReceipt {
  assertRecord(value, "FIGMA_COMPANION_RECEIPT");
  assertExactKeys(
    value,
    [
      "schemaVersion",
      "receiptId",
      "patchId",
      "patchHash",
      "idempotencyKey",
      "operatorConfirmationHash",
      "baseSource",
      "resultSource",
      "resultSelection",
      "operationReadbacks",
      "appliedBy",
      "appliedAt",
      "status",
      "contentHash",
    ],
    "FIGMA_COMPANION_RECEIPT",
  );
  if (value.schemaVersion !== "cogo.figma-companion-receipt.v1") {
    throw new Error("INVALID_FIGMA_COMPANION_RECEIPT_SCHEMA_VERSION");
  }
  assertBoundedString(value.receiptId, "FIGMA_COMPANION_RECEIPT_ID");
  assertBoundedString(value.patchId, "FIGMA_COMPANION_PATCH_ID");
  assertContentHash(value.patchHash, "FIGMA_COMPANION_PATCH");
  assertBoundedString(value.idempotencyKey, "FIGMA_COMPANION_IDEMPOTENCY_KEY");
  assertContentHash(
    value.operatorConfirmationHash,
    "FIGMA_COMPANION_OPERATOR_CONFIRMATION",
  );
  assertRecord(value.baseSource, "FIGMA_COMPANION_BASE_SOURCE");
  assertObservedSource(value.baseSource, "FIGMA_COMPANION_BASE_SOURCE");
  assertRecord(value.resultSource, "FIGMA_COMPANION_RESULT_SOURCE");
  assertObservedSource(value.resultSource, "FIGMA_COMPANION_RESULT_SOURCE", [
    "versionHistoryId",
  ]);
  assertBoundedString(
    value.resultSource.versionHistoryId,
    "FIGMA_COMPANION_VERSION_HISTORY_ID",
  );
  assertObservedSelection(value.resultSelection);
  if (value.resultSource.hash !== value.resultSelection.hash) {
    throw new Error("FIGMA_COMPANION_RESULT_SELECTION_HASH_MISMATCH");
  }
  if (value.resultSource.fileKey !== value.baseSource.fileKey) {
    throw new Error("FIGMA_COMPANION_RESULT_FILE_KEY_MISMATCH");
  }
  if (!Array.isArray(value.operationReadbacks)) {
    throw new Error("INVALID_FIGMA_COMPANION_OPERATION_READBACKS");
  }
  assertOperatorIdentity(value.appliedBy);
  assertIsoTimestamp(value.appliedAt, "FIGMA_COMPANION_APPLIED_AT");
  if (value.status !== "applied") {
    throw new Error("INVALID_FIGMA_COMPANION_RECEIPT_STATUS");
  }
  if (value.resultSource.version === value.baseSource.version) {
    throw new Error("FIGMA_COMPANION_NEW_SOURCE_VERSION_REQUIRED");
  }
  assertContentHash(value.contentHash, "FIGMA_COMPANION_RECEIPT");
  const { contentHash, ...base } = value;
  if (computeDesignContentHash(base) !== contentHash) {
    throw new Error("INVALID_FIGMA_COMPANION_RECEIPT_CONTENT_HASH");
  }
}

export function verifyFigmaCompanionReadback(
  patch: FigmaCompanionPatch,
  receipt: FigmaCompanionReceipt,
): void {
  assertFigmaCompanionPatch(patch);
  assertFigmaCompanionReceipt(receipt);
  if (
    receipt.patchId !== patch.patchId ||
    receipt.patchHash !== patch.contentHash
  ) {
    throw new Error("FIGMA_COMPANION_RECEIPT_PATCH_MISMATCH");
  }
  if (receipt.resultSource.fileKey !== patch.source.fileKey) {
    throw new Error("FIGMA_COMPANION_RECEIPT_RESULT_SOURCE_MISMATCH");
  }
  if (receipt.idempotencyKey !== patch.idempotencyKey) {
    throw new Error("FIGMA_COMPANION_RECEIPT_IDEMPOTENCY_KEY_MISMATCH");
  }
  if (
    receipt.baseSource.fileKey !== patch.source.fileKey ||
    receipt.baseSource.version !== patch.source.expectedVersion ||
    receipt.baseSource.hash !== patch.source.expectedHash
  ) {
    throw new Error("FIGMA_COMPANION_RECEIPT_BASE_SOURCE_MISMATCH");
  }
  assertResultSelection(patch, receipt.resultSelection);
  assertOperationReadbacks(patch.operations, receipt.operationReadbacks);
}

export function verifyFigmaCompanionOperationReadbacks(
  patch: FigmaCompanionPatch,
  readbacks: FigmaCompanionOperationReadback[],
): void {
  assertFigmaCompanionPatch(patch);
  assertOperationReadbacks(patch.operations, readbacks);
}

export function verifyFigmaCompanionLiveReplay(
  receipt: FigmaCompanionReceipt,
  observedSelection: FigmaCompanionObservedSelectionAuthority,
): void {
  assertFigmaCompanionReceipt(receipt);
  assertObservedSelection(observedSelection);
  if (
    !sameStrings(receipt.resultSelection.nodeIds, observedSelection.nodeIds) ||
    !sameStrings(
      receipt.resultSelection.tombstoneNodeIds,
      observedSelection.tombstoneNodeIds,
    ) ||
    receipt.resultSelection.hash !== observedSelection.hash
  ) {
    throw new Error("FIGMA_COMPANION_REPLAY_LIVE_STATE_CONFLICT");
  }
}

function assertCreatePatchInput(input: CreateFigmaCompanionPatchInput): void {
  assertBoundedString(input.patchId, "FIGMA_COMPANION_PATCH_ID");
  assertBoundedString(input.idempotencyKey, "FIGMA_COMPANION_IDEMPOTENCY_KEY");
  assertSourceAuthority(input.source);
  assertSelectionAuthority(input.selection, false);
  if (input.source.expectedHash !== input.selection.hash) {
    throw new Error("FIGMA_COMPANION_SOURCE_SELECTION_HASH_MISMATCH");
  }
  assertSemanticAuthority(input.semanticAuthority);
  assertActor(input.actor);
  assertBoundedString(
    input.reason,
    "FIGMA_COMPANION_REASON",
    MAX_REASON_LENGTH,
  );
  assertIsoTimestamp(input.requestedAt, "FIGMA_COMPANION_REQUESTED_AT");
  if (!Array.isArray(input.operations)) {
    throw new Error("INVALID_FIGMA_COMPANION_OPERATIONS");
  }
  if (
    input.operations.length < 1 ||
    input.operations.length > MAX_PATCH_OPERATIONS
  ) {
    throw new Error("FIGMA_COMPANION_OPERATIONS_OUT_OF_BOUNDS");
  }
  const operationIds = new Set<string>();
  const targetNodeIds = new Set<string>();
  const structuralParentOwners = new Map<string, string>();
  const selected = new Set(input.selection.nodeIds);
  for (const operation of input.operations) {
    assertOperation(operation);
    if (!selected.has(operation.nodeId)) {
      throw new Error("FIGMA_COMPANION_OPERATION_TARGET_NOT_SELECTED");
    }
    if (operationIds.has(operation.operationId)) {
      throw new Error("DUPLICATE_FIGMA_COMPANION_OPERATION_ID");
    }
    operationIds.add(operation.operationId);
    if (targetNodeIds.has(operation.nodeId)) {
      throw new Error("DUPLICATE_FIGMA_COMPANION_NODE_TARGET");
    }
    targetNodeIds.add(operation.nodeId);
    for (const parentNodeId of structuralParentNodeIds(operation)) {
      const owner = structuralParentOwners.get(parentNodeId);
      if (owner && owner !== operation.operationId) {
        throw new Error(
          `FIGMA_COMPANION_SHARED_STRUCTURAL_PARENT_UNSUPPORTED: ${parentNodeId}`,
        );
      }
      structuralParentOwners.set(parentNodeId, operation.operationId);
    }
  }
}

function structuralParentNodeIds(operation: FigmaCompanionOperation): string[] {
  switch (operation.kind) {
    case "delete-node":
      return [operation.expectedParentNodeId];
    case "reorder-node":
      return [operation.parentNodeId];
    case "reparent-node":
      return uniqueSorted([
        operation.expectedParentNodeId,
        operation.nextParentNodeId,
      ]);
    default:
      return [];
  }
}

function assertSourceAuthority(value: unknown): void {
  assertRecord(value, "FIGMA_COMPANION_SOURCE");
  assertExactKeys(
    value,
    [
      "fileKey",
      "expectedVersion",
      "expectedHash",
      "openPencilSourceId",
      "openPencilSourceRevision",
      "openPencilSourceSha256",
    ],
    "FIGMA_COMPANION_SOURCE",
  );
  assertBoundedString(value.fileKey, "FIGMA_COMPANION_FILE_KEY");
  assertBoundedString(value.expectedVersion, "FIGMA_COMPANION_SOURCE_VERSION");
  assertSha256(value.expectedHash, "FIGMA_COMPANION_SOURCE");
  assertBoundedString(
    value.openPencilSourceId,
    "FIGMA_COMPANION_OPENPENCIL_SOURCE_ID",
  );
  assertNonNegativeInteger(
    value.openPencilSourceRevision,
    "FIGMA_COMPANION_OPENPENCIL_SOURCE_REVISION",
  );
  assertSha256(
    value.openPencilSourceSha256,
    "FIGMA_COMPANION_OPENPENCIL_SOURCE",
  );
}

function assertSelectionAuthority(
  value: unknown,
  allowTombstones = true,
): void {
  assertRecord(value, "FIGMA_COMPANION_SELECTION");
  assertExactKeys(
    value,
    ["nodeIds", "hash", "tombstoneNodeIds"],
    "FIGMA_COMPANION_SELECTION",
  );
  assertCanonicalIds(value.nodeIds, "FIGMA_COMPANION_SELECTION_NODE_ID");
  assertSha256(value.hash, "FIGMA_COMPANION_SELECTION");
  if (value.tombstoneNodeIds !== undefined) {
    if (!allowTombstones) {
      throw new Error("FIGMA_COMPANION_BASE_SELECTION_TOMBSTONES_FORBIDDEN");
    }
    assertCanonicalIds(
      value.tombstoneNodeIds,
      "FIGMA_COMPANION_TOMBSTONE_NODE_ID",
      true,
    );
    const selected = new Set(value.nodeIds as string[]);
    for (const nodeId of value.tombstoneNodeIds as string[]) {
      if (!selected.has(nodeId)) {
        throw new Error("FIGMA_COMPANION_TOMBSTONE_NOT_SELECTED");
      }
    }
  }
}

function assertCheckpointNodeAuthorities(
  nodeIds: string[],
  value: unknown,
): asserts value is Record<string, FigmaCompanionNodeAuthority> {
  assertRecord(value, "FIGMA_COMPANION_CHECKPOINT_NODE_AUTHORITIES");
  if (!sameStrings(Object.keys(value).sort(), uniqueSorted(nodeIds))) {
    throw new Error("FIGMA_COMPANION_CHECKPOINT_NODE_AUTHORITY_SET_MISMATCH");
  }
  for (const nodeId of nodeIds) {
    const authority = value[nodeId];
    assertRecord(authority, "FIGMA_COMPANION_CHECKPOINT_NODE_AUTHORITY");
    assertExactKeys(
      authority,
      ["nodeHash", "parentNodeId", "index", "parentChildrenHash"],
      "FIGMA_COMPANION_CHECKPOINT_NODE_AUTHORITY",
    );
    assertSha256(authority.nodeHash, "FIGMA_COMPANION_CHECKPOINT_NODE");
    const hasParent = authority.parentNodeId !== null;
    if (hasParent) {
      assertBoundedString(
        authority.parentNodeId,
        "FIGMA_COMPANION_CHECKPOINT_PARENT_NODE_ID",
      );
      assertNonNegativeInteger(
        authority.index,
        "FIGMA_COMPANION_CHECKPOINT_PARENT_INDEX",
      );
      assertSha256(
        authority.parentChildrenHash,
        "FIGMA_COMPANION_CHECKPOINT_PARENT_CHILDREN",
      );
    } else if (
      authority.index !== null ||
      authority.parentChildrenHash !== null
    ) {
      throw new Error("FIGMA_COMPANION_CHECKPOINT_PARENT_AUTHORITY_MISMATCH");
    }
  }
}

function assertCanonicalIds(
  value: unknown,
  label: string,
  allowEmpty = false,
): asserts value is string[] {
  if (!Array.isArray(value) || (!allowEmpty && value.length < 1)) {
    throw new Error(`INVALID_${label}S`);
  }
  for (const id of value) assertBoundedString(id, label);
  if (new Set(value).size !== value.length) {
    throw new Error(`DUPLICATE_${label}`);
  }
  if (!sameStrings(value, uniqueSorted(value))) {
    throw new Error(`${label}S_NOT_CANONICAL`);
  }
}

function assertSemanticAuthority(value: unknown): void {
  assertRecord(value, "FIGMA_COMPANION_SEMANTIC_AUTHORITY");
  assertExactKeys(
    value,
    ["kind", "ref", "contentHash"],
    "FIGMA_COMPANION_SEMANTIC_AUTHORITY",
  );
  if (
    value.kind !== "cogo-design-mutation" &&
    value.kind !== "cogo-design-patch"
  ) {
    throw new Error("INVALID_FIGMA_COMPANION_SEMANTIC_AUTHORITY_KIND");
  }
  assertBoundedString(value.ref, "FIGMA_COMPANION_SEMANTIC_AUTHORITY_REF");
  assertContentHash(value.contentHash, "FIGMA_COMPANION_SEMANTIC_AUTHORITY");
}

function assertOperation(
  value: unknown,
): asserts value is FigmaCompanionOperation {
  assertRecord(value, "FIGMA_COMPANION_OPERATION");
  assertBoundedString(value.operationId, "FIGMA_COMPANION_OPERATION_ID");
  assertBoundedString(value.nodeId, "FIGMA_COMPANION_NODE_ID");
  assertBoundedString(value.expectedNodeType, "FIGMA_COMPANION_NODE_TYPE");
  assertSha256(value.expectedNodeHash, "FIGMA_COMPANION_EXPECTED_NODE");
  if (value.expectedName !== undefined) {
    assertBoundedString(value.expectedName, "FIGMA_COMPANION_NODE_NAME");
  }
  switch (value.kind) {
    case "rename-node":
      assertExactKeys(
        value,
        operationKeys("nextValue"),
        "FIGMA_COMPANION_OPERATION",
      );
      assertBoundedString(value.nextValue, "FIGMA_COMPANION_NEXT_NODE_NAME");
      return;
    case "set-text":
      assertExactKeys(
        value,
        operationKeys("nextValue"),
        "FIGMA_COMPANION_OPERATION",
      );
      if (
        typeof value.nextValue !== "string" ||
        value.nextValue.length > MAX_TEXT_LENGTH
      ) {
        throw new Error("INVALID_FIGMA_COMPANION_NEXT_TEXT");
      }
      return;
    case "set-visible":
      assertExactKeys(
        value,
        operationKeys("nextValue"),
        "FIGMA_COMPANION_OPERATION",
      );
      if (typeof value.nextValue !== "boolean") {
        throw new Error("INVALID_FIGMA_COMPANION_VISIBILITY");
      }
      return;
    case "set-opacity":
      assertExactKeys(
        value,
        operationKeys("nextValue"),
        "FIGMA_COMPANION_OPERATION",
      );
      if (
        typeof value.nextValue !== "number" ||
        !Number.isFinite(value.nextValue) ||
        value.nextValue < 0 ||
        value.nextValue > 1
      ) {
        throw new Error("INVALID_FIGMA_COMPANION_OPACITY");
      }
      return;
    case "set-geometry":
      assertExactKeys(
        value,
        operationKeys("nextValue"),
        "FIGMA_COMPANION_OPERATION",
      );
      assertGeometry(value.nextValue);
      return;
    case "delete-node":
      assertExactKeys(
        value,
        operationKeys(
          "expectedParentNodeId",
          "expectedIndex",
          "expectedParentChildrenHash",
        ),
        "FIGMA_COMPANION_OPERATION",
      );
      assertBoundedString(
        value.expectedParentNodeId,
        "FIGMA_COMPANION_EXPECTED_PARENT_NODE_ID",
      );
      assertNonNegativeInteger(
        value.expectedIndex,
        "FIGMA_COMPANION_EXPECTED_INDEX",
      );
      assertSha256(
        value.expectedParentChildrenHash,
        "FIGMA_COMPANION_EXPECTED_PARENT_CHILDREN",
      );
      return;
    case "reparent-node":
      assertExactKeys(
        value,
        operationKeys(
          "expectedParentNodeId",
          "expectedIndex",
          "expectedParentChildrenHash",
          "nextParentNodeId",
          "expectedNextParentChildrenHash",
          "nextIndex",
        ),
        "FIGMA_COMPANION_OPERATION",
      );
      assertBoundedString(
        value.expectedParentNodeId,
        "FIGMA_COMPANION_EXPECTED_PARENT_NODE_ID",
      );
      assertNonNegativeInteger(
        value.expectedIndex,
        "FIGMA_COMPANION_EXPECTED_INDEX",
      );
      assertSha256(
        value.expectedParentChildrenHash,
        "FIGMA_COMPANION_EXPECTED_PARENT_CHILDREN",
      );
      assertBoundedString(
        value.nextParentNodeId,
        "FIGMA_COMPANION_NEXT_PARENT_NODE_ID",
      );
      assertSha256(
        value.expectedNextParentChildrenHash,
        "FIGMA_COMPANION_EXPECTED_NEXT_PARENT_CHILDREN",
      );
      assertNonNegativeInteger(value.nextIndex, "FIGMA_COMPANION_NEXT_INDEX");
      return;
    case "reorder-node":
      assertExactKeys(
        value,
        operationKeys(
          "parentNodeId",
          "expectedIndex",
          "expectedParentChildrenHash",
          "nextIndex",
        ),
        "FIGMA_COMPANION_OPERATION",
      );
      assertBoundedString(value.parentNodeId, "FIGMA_COMPANION_PARENT_NODE_ID");
      assertNonNegativeInteger(
        value.expectedIndex,
        "FIGMA_COMPANION_EXPECTED_INDEX",
      );
      assertSha256(
        value.expectedParentChildrenHash,
        "FIGMA_COMPANION_EXPECTED_PARENT_CHILDREN",
      );
      assertNonNegativeInteger(value.nextIndex, "FIGMA_COMPANION_NEXT_INDEX");
      return;
    default:
      throw new Error("INVALID_FIGMA_COMPANION_OPERATION_KIND");
  }
}

function assertGeometry(value: unknown): void {
  assertRecord(value, "FIGMA_COMPANION_GEOMETRY");
  assertExactKeys(
    value,
    ["x", "y", "width", "height", "rotation"],
    "FIGMA_COMPANION_GEOMETRY",
  );
  const keys = ["x", "y", "width", "height", "rotation"] as const;
  if (!keys.some((key) => value[key] !== undefined)) {
    throw new Error("FIGMA_COMPANION_GEOMETRY_CHANGE_REQUIRED");
  }
  for (const key of keys) {
    const field = value[key];
    if (
      field !== undefined &&
      (typeof field !== "number" || !Number.isFinite(field))
    ) {
      throw new Error(`INVALID_FIGMA_COMPANION_GEOMETRY_${key.toUpperCase()}`);
    }
  }
  for (const key of ["x", "y"] as const) {
    if (
      typeof value[key] === "number" &&
      Math.abs(value[key]) > MAX_COORDINATE
    ) {
      throw new Error(`INVALID_FIGMA_COMPANION_GEOMETRY_${key.toUpperCase()}`);
    }
  }
  for (const key of ["width", "height"] as const) {
    if (
      typeof value[key] === "number" &&
      (value[key] <= 0 || value[key] > MAX_DIMENSION)
    ) {
      throw new Error("INVALID_FIGMA_COMPANION_GEOMETRY_SIZE");
    }
  }
  if (
    typeof value.rotation === "number" &&
    Math.abs(value.rotation) > MAX_ROTATION
  ) {
    throw new Error("INVALID_FIGMA_COMPANION_GEOMETRY_ROTATION");
  }
}

function requiredOperatorScopes(
  operations: FigmaCompanionOperation[],
): FigmaCompanionOperatorScope[] {
  const scopes = new Set<FigmaCompanionOperatorScope>(["apply"]);
  for (const operation of operations) {
    if (
      operation.kind === "reparent-node" ||
      operation.kind === "reorder-node" ||
      operation.kind === "delete-node"
    ) {
      scopes.add("structural");
    }
    if (operation.kind === "delete-node") scopes.add("destructive");
  }
  return [...scopes].sort() as FigmaCompanionOperatorScope[];
}

function assertConfirmationForPatch(
  patch: FigmaCompanionPatch,
  confirmation: FigmaCompanionOperatorConfirmation,
): void {
  if (
    confirmation.patchId !== patch.patchId ||
    confirmation.patchHash !== patch.contentHash
  ) {
    throw new Error("FIGMA_COMPANION_OPERATOR_CONFIRMATION_PATCH_MISMATCH");
  }
  assertRequiredOperatorScopes(patch, confirmation.scopes);
}

function assertRequiredOperatorScopes(
  patch: FigmaCompanionPatch,
  scopes: FigmaCompanionOperatorScope[],
): void {
  for (const required of patch.requiredOperatorScopes) {
    if (!scopes.includes(required)) {
      throw new Error(
        `FIGMA_COMPANION_OPERATOR_${required.toUpperCase()}_CONFIRMATION_REQUIRED`,
      );
    }
  }
}

function assertOperatorScopes(value: unknown): void {
  if (!Array.isArray(value) || value.length < 1) {
    throw new Error("INVALID_FIGMA_COMPANION_OPERATOR_SCOPES");
  }
  for (const scope of value) {
    if (
      scope !== "apply" &&
      scope !== "structural" &&
      scope !== "destructive"
    ) {
      throw new Error("INVALID_FIGMA_COMPANION_OPERATOR_SCOPE");
    }
  }
  if (new Set(value).size !== value.length) {
    throw new Error("DUPLICATE_FIGMA_COMPANION_OPERATOR_SCOPE");
  }
}

function assertOperatorIdentity(
  value: unknown,
): asserts value is FigmaCompanionOperatorIdentity {
  assertRecord(value, "FIGMA_COMPANION_OPERATOR_IDENTITY");
  assertExactKeys(
    value,
    ["source", "userId", "userName"],
    "FIGMA_COMPANION_OPERATOR_IDENTITY",
  );
  if (value.source !== "figma-current-user") {
    throw new Error("INVALID_FIGMA_COMPANION_OPERATOR_IDENTITY_SOURCE");
  }
  assertBoundedString(value.userId, "FIGMA_COMPANION_OPERATOR_USER_ID");
  assertBoundedString(value.userName, "FIGMA_COMPANION_OPERATOR_USER_NAME");
}

function assertObservedSource(
  value: unknown,
  label: string,
  extraKeys: string[] = [],
): void {
  assertRecord(value, label);
  assertExactKeys(value, ["fileKey", "version", "hash", ...extraKeys], label);
  assertBoundedString(value.fileKey, `${label}_FILE_KEY`);
  assertBoundedString(value.version, `${label}_VERSION`);
  assertSha256(value.hash, label);
}

function assertObservedSelection(
  value: unknown,
): asserts value is FigmaCompanionObservedSelectionAuthority {
  assertSelectionAuthority(value, true);
  if (!("tombstoneNodeIds" in (value as Record<string, unknown>))) {
    throw new Error("INVALID_FIGMA_COMPANION_TOMBSTONE_NODE_IDS");
  }
}

function assertResultSelection(
  patch: FigmaCompanionPatch,
  selection: FigmaCompanionObservedSelectionAuthority,
): void {
  const expectedTombstones = uniqueSorted(
    patch.operations
      .filter((operation) => operation.kind === "delete-node")
      .map((operation) => operation.nodeId),
  );
  if (
    !sameStrings(selection.nodeIds, patch.selection.nodeIds) ||
    !sameStrings(selection.tombstoneNodeIds, expectedTombstones)
  ) {
    throw new Error("FIGMA_COMPANION_RESULT_SELECTION_AUTHORITY_MISMATCH");
  }
}

function assertOperationReadbacks(
  operations: FigmaCompanionOperation[],
  readbacks: FigmaCompanionOperationReadback[],
): void {
  if (!Array.isArray(readbacks) || readbacks.length !== operations.length) {
    throw new Error("FIGMA_COMPANION_OPERATION_READBACK_COUNT_MISMATCH");
  }
  const byId = new Map(
    readbacks.map((readback) => [readback.operationId, readback]),
  );
  if (byId.size !== readbacks.length) {
    throw new Error("DUPLICATE_FIGMA_COMPANION_OPERATION_READBACK");
  }
  for (const operation of operations) {
    const readback = byId.get(operation.operationId);
    if (readback) {
      assertRecord(readback, "FIGMA_COMPANION_OPERATION_READBACK");
      assertExactKeys(
        readback,
        ["operationId", "kind", "nodeId", "observedValue"],
        "FIGMA_COMPANION_OPERATION_READBACK",
      );
    }
    if (
      !readback ||
      readback.kind !== operation.kind ||
      readback.nodeId !== operation.nodeId
    ) {
      throw new Error("FIGMA_COMPANION_OPERATION_READBACK_MISMATCH");
    }
    assertReadbackValue(operation, readback.observedValue);
  }
}

function assertReadbackValue(
  operation: FigmaCompanionOperation,
  observedValue: unknown,
): void {
  switch (operation.kind) {
    case "rename-node":
    case "set-text":
    case "set-visible":
    case "set-opacity":
      if (!sameJson(observedValue, operation.nextValue)) {
        throw new Error("FIGMA_COMPANION_OPERATION_READBACK_VALUE_MISMATCH");
      }
      return;
    case "set-geometry": {
      assertRecord(observedValue, "FIGMA_COMPANION_GEOMETRY_READBACK");
      for (const [key, value] of Object.entries(operation.nextValue)) {
        if (!sameJson(observedValue[key], value)) {
          throw new Error("FIGMA_COMPANION_OPERATION_READBACK_VALUE_MISMATCH");
        }
      }
      return;
    }
    case "delete-node":
      if (!sameJson(observedValue, { exists: false })) {
        throw new Error("FIGMA_COMPANION_OPERATION_READBACK_VALUE_MISMATCH");
      }
      return;
    case "reparent-node":
      if (
        !sameJson(observedValue, {
          parentNodeId: operation.nextParentNodeId,
          index: operation.nextIndex,
        })
      ) {
        throw new Error("FIGMA_COMPANION_OPERATION_READBACK_VALUE_MISMATCH");
      }
      return;
    case "reorder-node":
      if (
        !sameJson(observedValue, {
          parentNodeId: operation.parentNodeId,
          index: operation.nextIndex,
        })
      ) {
        throw new Error("FIGMA_COMPANION_OPERATION_READBACK_VALUE_MISMATCH");
      }
  }
}

function normalizeOperation(
  operation: FigmaCompanionOperation,
): FigmaCompanionOperation {
  return cloneJsonValue(operation) as FigmaCompanionOperation;
}

function operationKeys(...extra: string[]): string[] {
  return [
    "operationId",
    "kind",
    "nodeId",
    "expectedNodeType",
    "expectedName",
    "expectedNodeHash",
    ...extra,
  ];
}

function assertActor(value: unknown): asserts value is DesignMutationActor {
  assertRecord(value, "FIGMA_COMPANION_ACTOR");
  assertExactKeys(value, ["kind", "id"], "FIGMA_COMPANION_ACTOR");
  if (value.kind !== "human" && value.kind !== "agent") {
    throw new Error("INVALID_FIGMA_COMPANION_ACTOR_KIND");
  }
  assertBoundedString(value.id, "FIGMA_COMPANION_ACTOR_ID");
}

function assertBoundedString(
  value: unknown,
  label: string,
  maxLength = MAX_ID_LENGTH,
): asserts value is string {
  if (
    typeof value !== "string" ||
    value.trim().length === 0 ||
    value.length > maxLength
  ) {
    throw new Error(`INVALID_${label}`);
  }
}

function assertNonNegativeInteger(value: unknown, label: string): void {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
    throw new Error(`INVALID_${label}`);
  }
}

function assertSha256(value: unknown, label: string): asserts value is string {
  if (typeof value !== "string" || !SHA256_DIGEST_PATTERN.test(value)) {
    throw new Error(`INVALID_${label}_SHA256`);
  }
}

function assertContentHash(
  value: unknown,
  label: string,
): asserts value is string {
  if (typeof value !== "string" || !CONTENT_HASH_PATTERN.test(value)) {
    throw new Error(`INVALID_${label}_CONTENT_HASH`);
  }
}

function assertIsoTimestamp(value: unknown, label: string): void {
  assertBoundedString(value, label);
  const timestamp = Date.parse(value);
  if (
    !Number.isFinite(timestamp) ||
    new Date(timestamp).toISOString() !== value
  ) {
    throw new Error(`INVALID_${label}`);
  }
}

function assertRecord(
  value: unknown,
  label: string,
): asserts value is Record<string, unknown> {
  if (
    value === null ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    Object.getPrototypeOf(value) !== Object.prototype
  ) {
    throw new Error(`INVALID_${label}`);
  }
}

function assertExactKeys(
  value: object,
  allowedKeys: readonly string[],
  label: string,
): void {
  const allowed = new Set(allowedKeys);
  for (const key of Object.keys(value)) {
    if (!allowed.has(key)) throw new Error(`UNKNOWN_${label}_FIELD: ${key}`);
  }
}

function assertPayloadSize(value: unknown): void {
  const bytes = new TextEncoder().encode(JSON.stringify(value)).byteLength;
  if (bytes > MAX_PATCH_BYTES) {
    throw new Error("FIGMA_COMPANION_PATCH_PAYLOAD_TOO_LARGE");
  }
}

function uniqueSorted<T extends string>(values: T[]): T[] {
  return [...new Set(values)].sort();
}

function sameStrings(left: string[], right: string[]): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}

function cloneJsonValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function sameJson(left: unknown, right: unknown): boolean {
  return computeDesignContentHash(left) === computeDesignContentHash(right);
}
